import React from "react";
import * as analytics from "../../Util/Analytics/TableAnalytics";
import { isSpecialCode, parseNumber, isCensusSite } from "../../Util/SuppressionRules";

// Format percentage with new suppression rules
function fmtPercent(v, dataValue = null) {
  // Use the provided dataValue if available, otherwise use v
  const checkValue = dataValue ?? v;

  // Suppression codes take priority: show em-dash
  if (isSpecialCode(checkValue)) return "—";

  // Missing data: show em-dash
  if (v === null || v === undefined || v === '') return "—";
  
  // Parse numeric value
  const numVal = parseNumber(checkValue ?? v);
  if (numVal === null || isNaN(numVal)) return "—";
  
  // 0 or 100 (when not suppressed): show without decimals
  if (numVal === 0 || numVal === 100) return String(Math.round(numVal));
  
  // Valid data: format to 1 decimal place
  return numVal.toFixed(1);
}

function isNAValue(v) {
  if (v === null || v === undefined) return false;
  const s = String(v).trim().toUpperCase();
  return s === "N/A" || s === "NA";
}

// Format CI with new suppression rules
// Returns string for display, or null if suppressed (signaling caller to show footnote marker)
function fmtCI(low, high, percent = null, dataValue = null, isCensus = false, ciValue = null) {
  // Use the provided dataValue or percent for suppression checks
  const checkValue = dataValue ?? percent;
  
  // Census sites: CI not calculated
  if (isCensus) return "N/A";

  // Some payloads send CI directly as text (for example "N/A").
  if (isNAValue(ciValue) || isNAValue(low) || isNAValue(high)) return "N/A";
  
  // Missing data: show em-dash
  if (checkValue === null || checkValue === undefined || checkValue === '') return "—";
  
  // Suppression codes take priority: show em-dash
  if (isSpecialCode(checkValue)) return "—";

  const numVal = parseNumber(checkValue);
  if (numVal !== null && !isNaN(numVal) && (numVal === 0 || numVal === 100)) {
    return "N/A";
  }
  
  // Treat dash placeholders in CI fields as missing
  const lowStr = low !== undefined && low !== null ? String(low).trim() : "";
  const highStr = high !== undefined && high !== null ? String(high).trim() : "";
  const ciStr = ciValue !== undefined && ciValue !== null ? String(ciValue).trim() : "";
  const lowIsDash = lowStr === "—" || lowStr === "-" || lowStr === "--" || lowStr === "–";
  const highIsDash = highStr === "—" || highStr === "-" || highStr === "--" || highStr === "–";
  const ciIsDash = ciStr === "—" || ciStr === "-" || ciStr === "--" || ciStr === "–";
  const lowMissing = low === null || low === undefined || lowStr === "";
  const highMissing = high === null || high === undefined || highStr === "";
  const ciMissing = ciValue === null || ciValue === undefined || ciStr === "";

  // Valid data: format CI range or show em-dash if missing
  if (lowMissing || highMissing || lowIsDash || highIsDash) {
    // If CI is entirely absent (all fields null/empty) but value is present,
    // display N/A instead of em-dash.
    if (lowMissing && highMissing && ciMissing) {
      return "N/A";
    }

    // Fallback for payloads that only provide CI as a range string.
    if (ciStr && !ciIsDash) {
      // Check if ciStr is "N/A" before trying to parse as numeric range
      if (isNAValue(ciStr)) {
        return "N/A";
      }
      const parts = ciStr.match(/([+-]?\d*\.?\d+)\s*[-–]\s*([+-]?\d*\.?\d+)/);
      if (parts && parts.length === 3) {
        const lowParsed = parseNumber(parts[1]);
        const highParsed = parseNumber(parts[2]);
        if (lowParsed !== null && highParsed !== null && !isNaN(lowParsed) && !isNaN(highParsed)) {
          return `${lowParsed.toFixed(1)} - ${highParsed.toFixed(1)}`;
        }
      }
    }
    return "—";
  }
  const lowNum = parseNumber(low);
  const highNum = parseNumber(high);
  if (lowNum === null || highNum === null || isNaN(lowNum) || isNaN(highNum)) return "—";
  
  return `${lowNum.toFixed(1)} - ${highNum.toFixed(1)}`;
}

function fmtN(n, dataValue = null) {
  if (n == null) return "—";
  return n;
}

function fmtP(p) {
  if (p == null) return "—";
  const v = Number(p);
  // Display with a leading zero (e.g., 0.50) for clarity
  return v.toFixed(2);
}

export default function ComparisonTableRow({
  row,
  showSampleSize,
  years,
  onSubQuestionChange,
  questionId,
  onLocationChange,
  variant = "view1",
  topicColor,
  showPValue = true,
}) {
  const { Location, YearA, YearB, PValue, Direction, HasDataBoth, Label, SubQuestion } = row || {};

  const moreA = HasDataBoth && PValue != null && Direction === "A>B";
  const moreB = HasDataBoth && PValue != null && Direction === "B>A";
  const noDiff = HasDataBoth && PValue != null && Direction === "NoDiff";

  const Dot = () => <span className="cmp-dot" style={topicColor ? { color: topicColor } : undefined} aria-hidden>●</span>;

  let rowLabel;
  if (variant === "view3") {
    // View 3: first column should be the Year only
    // Be resilient to API field naming: Year vs Yr; also accept flat Year/Yr or Label
    const yearVal = (
      row?.DisplayYear ??
      row?.YearA?.Year ?? row?.YearA?.Yr ??
      row?.YearB?.Year ?? row?.YearB?.Yr ??
      row?.Year ?? row?.Yr ?? Label
    );
    rowLabel = yearVal != null ? yearVal : "";
  } else {
    // View 1/2: prefer human-friendly label, but avoid blank cells.
    // Fallback order if missing: use any available year, then identifiers.
    rowLabel = (
      Label ||
      SubQuestion ||
      Location?.Desc ||
      row?.DisplayYear ||
      row?.YearA?.Year || row?.YearA?.Yr ||
      row?.YearB?.Year || row?.YearB?.Yr ||
      row?.Year || row?.Yr ||
      row?.SubQuestionId ||
      Location?.Abbr ||
      ""
    );
  }
  const baseCensus = isCensusSite(row?.Location || row);
  const censusA = baseCensus || isCensusSite(YearA) || isCensusSite(YearA?.GeoLocation);
  const censusB = baseCensus || isCensusSite(YearB) || isCensusSite(YearB?.GeoLocation);

  const yearAPercent = YearA?.Percent ?? YearA?.NPercent;
  const yearADataValue = YearA?.DataValue ?? YearA?.DataValueRaw ?? YearA?.NPercent ?? YearA?.Percent;
  const yearALow = YearA?.CILow ?? YearA?.LowCI ?? YearA?.Lower ?? YearA?.LowerValue;
  const yearAHigh = YearA?.CIHigh ?? YearA?.HighCI ?? YearA?.Upper ?? YearA?.UpperValue;
  const yearACIValue = YearA?.ConfidenceInterval ?? YearA?.CI;

  const yearBPercent = YearB?.Percent ?? YearB?.NPercent;
  const yearBDataValue = YearB?.DataValue ?? YearB?.DataValueRaw ?? YearB?.NPercent ?? YearB?.Percent;
  const yearBLow = YearB?.CILow ?? YearB?.LowCI ?? YearB?.Lower ?? YearB?.LowerValue;
  const yearBHigh = YearB?.CIHigh ?? YearB?.HighCI ?? YearB?.Upper ?? YearB?.UpperValue;
  const yearBCIValue = YearB?.ConfidenceInterval ?? YearB?.CI;

  return (
    <tr>
      <th className="th-secondary" style={{ textAlign: "left" }}>
        {variant !== "view3" && onSubQuestionChange && row?.SubQuestionId && SubQuestion ? (
          <button
            className="btn btn-link m-0 p-0 text-left"
            onClick={(e) => {
              e.preventDefault();
              analytics.selectTableSubQuestion({
                questionId: questionId,
                subQuestionId: row.SubQuestionId,
              });
              onSubQuestionChange(row.SubQuestionId);
            }}
          >
            {rowLabel}
          </button>
        ) : variant === "view1" && onLocationChange && row?.Location?.Abbr ? (
          <button
            className="btn btn-link m-0 p-0 text-left"
            onClick={(e) => {
              e.preventDefault();
              try {
                const locationId =
                  row?.Location?.Abbr ||
                  row?.Location?.LocationAbbr ||
                  row?.LocationAbbr;
                analytics.selectionTableLocation({
                  locationAbbr: row.Location.Abbr,
                  locationId,
                });
              } catch (_) {}
              onLocationChange(
                {
                  LocationAbbr: row.Location.Abbr,
                  LocationDesc: row.Location.Desc,
                  LocationType: row.Location.Type,
                },
                "table"
              );
            }}
          >
            {rowLabel}
          </button>
        ) : (
          <>{rowLabel}</>
        )}
      </th>
      {/* Year A (primary selected year) first */}
      {showSampleSize && <td>{fmtN(YearA?.SampleSize, yearADataValue)}</td>}
      <td>{fmtPercent(yearAPercent, yearADataValue)}</td>
      <td>{fmtCI(yearALow, yearAHigh, yearAPercent, yearADataValue, censusA, yearACIValue)}</td>

      {/* Year B (comparison year) second */}
      {showSampleSize && <td>{fmtN(YearB?.SampleSize, yearBDataValue)}</td>}
      <td>{fmtPercent(yearBPercent, yearBDataValue)}</td>
      <td>{fmtCI(yearBLow, yearBHigh, yearBPercent, yearBDataValue, censusB, yearBCIValue)}</td>
      {showPValue && <td className="text-center">{fmtP(PValue)}</td>}
      {/* Significance columns: [YearA > YearB] [YearB > YearA] [NoDiff] */}
      <td className="text-center">{moreA ? <Dot /> : null}</td>
      <td className="text-center">{moreB ? <Dot /> : null}</td>
      <td className="text-center">{noDiff ? <Dot /> : null}</td>
    </tr>
  );
}
